import React, { useMemo } from 'react'
import type { PresentationLibrary, QuoteFile } from 'paintscout'
import { getImageWidth, getColumnWidth, gridPrintStyles } from '../../../FilesGrid/filesGridLayout'
import CancelIcon from '@material-ui/icons/Cancel'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import EditIcon from '@material-ui/icons/Edit'
import type { DropzoneProps, PresentationFilePreviewProps as FilePreviewProps, WithStyles } from '@paintscout/ui'
import {
  createStyles,
  Tooltip,
  withStyles,
  Button,
  Grid,
  Typography,
  useMediaQuery,
  PresentationFilePreview as FilePreview,
  PresentationDropzone as Dropzone
} from '@paintscout/ui'
import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc'
import { useConnection } from 'react-detect-offline'
import { TRIAL_QUOTE_FILES_LIMIT } from '@paintscout/util'
import { useQuote } from '../../../context'
import { getFiles } from '@paintscout/util/builder'
import PublishIcon from '@material-ui/icons/Publish'
import { InlineEditableMedia } from '../../inlineEditable'
import { useUser } from '@paintscout/ui-business'
import classnames from 'classnames'

/** Marks the synthetic, always-first company-logo tile PresentationLibraryDialog prepends into
 * `files` — it isn't a real library entry, so its delete affordance is hidden here rather than
 * relying on the caller's `onDelete` to no-op silently. */
export const COMPANY_LOGO_FILE_KEY = '__company-logo__'

/** `src` isn't guaranteed unique (e.g. duplicate placeholders), so it's never used as a key.
 * Falls back to array index rather than `src` when a file has neither `key` nor `tempKey`. */
function getFileKey(file: QuoteFile, index: number): string {
  return file?.key ? file.key : file?.tempKey ?? `gallery-file-${index}`
}

export interface PresentationFilesGridProps {
  files: QuoteFile[]
  isSettings?: boolean
  isEditable?: boolean
  handleEditFile?: (field: string, content: QuoteFile) => void
  mediaOptionsProps?: {
    blockPrefix: string
  }
  editable?: boolean
  /** Grid indexes rendered as selected (multi-select pickers — see PresentationLibraryDialog). */
  selectedIndexes?: number[]
  /**
   * Extra files to add alongside the one a tile is filling — a tile's picker can return several
   * files at once (#23709), and everything past the first belongs to the owning block, not the tile.
   */
  onAddFiles?: (files: QuoteFile[]) => void
  showUploadDropzone?: boolean
  onUploadClick?: (event: React.MouseEvent) => void
  /**
   * Where the Add Media tile sits relative to the file tiles. `'start'` puts it first, so a long
   * library doesn't have to be scrolled past to add to it (#23713). Defaults to `'end'`, which is
   * what every grid outside PresentationLibraryDialog still uses.
   */
  uploadButtonPosition?: 'start' | 'end'
  responsiveTransformation?: boolean
  columns?: number
  mobileColumns?: number
  showBorder?: boolean
  fullItemHeight?: boolean
  showDetails?: boolean
  justify?: 'space-around' | 'space-between' | 'center' | 'flex-start' | 'flex-end'
  inPresentation?: boolean
  presentationLibrary?: {
    addFilesToLibrary: (args: { files: QuoteFile[] }) => void
    library: PresentationLibrary
    saveLibrary: (args: { library: PresentationLibrary }) => void
  }

  onReorder?: (files: QuoteFile[]) => any
  onUpload?: (files: File[]) => any
  onFileClick?: (index: number) => any
  onDelete?: (index: number) => void
  onEdit?: (index: number) => void
  onLoadMore?: () => any

  filePreviewProps?: Partial<FilePreviewProps>
}

type PresentationFilesGridPropsWithInjections = PresentationFilesGridProps & WithStyles<typeof styles>

const styles = createStyles({
  root: {
    // Safari starts a native text/image selection from the mousedown+mousemove that happens
    // before react-sortable-hoc's pressDelay elapses and it calls preventDefault, highlighting
    // sibling tiles while dragging a photo (#23710).
    userSelect: 'none'
  },
  dragging: {
    zIndex: 2_147_483_002
  },
  ...gridPrintStyles,
  addButton: {
    aspectRatio: '1/1'
  }
})

const SortableFilePreview: React.ComponentType<any> = withStyles({
  filePreview: {
    height: '100%',
    width: '100%',
    flexGrow: 1,
    borderRadius: 3,
    objectFit: 'cover',
    aspectRatio: '1/1',
    userDrag: 'none',
    userSelect: 'none'
  },
  ...gridPrintStyles,
  itemInner: ({ showBorder }: { showBorder: boolean }) => {
    const baseStyle = {
      height: '100%',
      display: 'flex'
    }

    if (showBorder === false) {
      return {
        ...baseStyle
      }
    }

    return {
      height: '100%',
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'center',
      background: 'white',
      ...baseStyle
    }
  },

  imageWrapper: () => {
    return {
      position: 'relative'
    }
  },
  imageWrapperItem: ({ fullItemHeight }: { fullItemHeight: boolean }) => {
    return {
      maxHeight: '100%',
      ...(fullItemHeight && { height: '100%' })
    }
  },
  item: { aspectRatio: '1/1' },
  caption: {
    marginBottom: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  deleteIcon: {
    cursor: 'pointer',
    position: 'absolute',
    zIndex: 2,
    height: 28,
    width: 28,
    top: -13,
    right: -13,
    color: '#fff',
    background: 'radial-gradient(circle, rgba(0,0,0,1) 50%, rgba(255,255,255,1) 50%);',
    border: '1px solid #000',
    borderRadius: '50%',
    overflow: 'hidden',
    '&:hover': {
      color: '#eee',
      background: 'radial-gradient(circle, rgba(0,0,0,1) 50%, rgba(238,238,238,1) 50%);'
    }
  },
  detailsBlock: {
    flexBasis: 0
  },
  editButton: {
    '& svg': {
      height: 14,
      marginRight: -5
    }
  },
  onClickWrapper: {
    cursor: 'pointer',
    height: 'inherit'
  },
  selected: {
    outline: '3px solid #2E71EC',
    outlineOffset: 2
  },
  selectedIcon: {
    position: 'absolute',
    zIndex: 2,
    top: -10,
    right: -10,
    height: 26,
    width: 26,
    color: '#2E71EC',
    background: '#fff',
    borderRadius: '50%'
  }
})(
  SortableElement(
    ({
      file,
      fileIndex,
      classes,
      columns,
      mobileColumns,
      showDetails,
      isSettings,
      isEditable,
      inPresentation,
      handleEditFile,
      mediaOptionsProps,
      presentationLibrary,
      onAddFiles,
      onClick,
      onDelete,
      onEdit,
      selected,
      filePreviewProps
    }: {
      file?: QuoteFile
      fileIndex: number
      columns: number
      mobileColumns: number
      showDetails: boolean
      isSettings: boolean
      isEditable: boolean
      inPresentation: boolean
      presentationLibrary?: {
        addFilesToLibrary: (args: { files: QuoteFile[] }) => void
        library: PresentationLibrary
        saveLibrary: (args: { library: PresentationLibrary }) => void
      }
      handleEditFile: (field: string, content: QuoteFile) => void
      mediaOptionsProps?: {
        blockPrefix: string
      }
      classes: any
      onAddFiles?: (files: QuoteFile[]) => void
      onClick?: () => any
      onDelete?: (index: number) => any
      onEdit?: (index: number) => void
      selected?: boolean
      filePreviewProps?: Partial<FilePreviewProps>
    }) => {
      const { blockPrefix } = mediaOptionsProps || {}
      // to prevent rendering placeholders on CV/Preview
      // can't return null for a sortable element, queue this semi-hacky solution
      if (!isSettings && !file) return <div style={{ display: 'none' }}></div>

      let transformationProps: any = {
        ...getImageWidth(columns),
        crop: 'pad',
        background: '#fff'
      }

      if (file?.type?.startsWith('video')) {
        transformationProps = {
          ...transformationProps,
          controls: false
        }
      }

      const { columnWidth, columnClass } = getColumnWidth(columns, classes)
      const { columnWidth: mobileColumnWidth } = getColumnWidth(mobileColumns, classes)

      const handleEdit = (file: QuoteFile) => {
        // this gets called from onFileClick as in EditItemDialog
        // the if prevents trying to call an undefined function
        if (handleEditFile) handleEditFile(`fields.files.[${fileIndex}]`, file)
      }

      return (
        <Grid
          item
          key={getFileKey(file, fileIndex)}
          xs={mobileColumns ? mobileColumnWidth : 12}
          sm={columnWidth}
          md={columnWidth}
          className={columnClass}
        >
          <div className={classes.itemInner}>
            <Grid container classes={{ container: classes.imageWrapper }} alignItems="center">
              <Grid item xs={12} classes={{ root: classes.imageWrapperItem }}>
                {selected && <CheckCircleIcon className={classes.selectedIcon} data-testid="selected-badge" />}
                {onDelete && !selected && file?.key !== COMPANY_LOGO_FILE_KEY && (
                  <CancelIcon
                    className={classes.deleteIcon}
                    onClick={() => onDelete(fileIndex)}
                    data-testid="delete-button"
                  />
                )}
                {inPresentation && (
                  <div className={classes.onClickWrapper} onClick={onClick}>
                    <InlineEditableMedia
                      mediaOptionsProps={{
                        blockPrefix: blockPrefix,
                        fieldSuffix: `files.[${fileIndex}]`
                      }}
                      classes={{ image: classes.filePreview }}
                      file={file?.src ? file : null}
                      handleEdit={(file) => handleEdit(file)}
                      isEditable={isEditable}
                      isSettings={isSettings}
                      placeholderButtonText={'Add Media'}
                      presentationLibrary={presentationLibrary}
                      onAddExtraFiles={onAddFiles}
                      square={true}
                      filePreviewProps={filePreviewProps}
                    />
                  </div>
                )}
                {!inPresentation && (
                  <FilePreview
                    classes={{ image: classnames(classes.filePreview, { [classes.selected]: selected }) }}
                    file={file}
                    videoThumbnail={true}
                    onClick={onClick}
                    {...transformationProps}
                    showAllPages={false}
                    inGallery={true}
                    inPresentation={false}
                    {...filePreviewProps}
                  />
                )}
                {onEdit && (
                  <Button
                    onClick={() => onEdit(fileIndex)}
                    startIcon={<EditIcon />}
                    classes={{ root: classes.editButton }}
                  >
                    Replace
                  </Button>
                )}
                {showDetails && (
                  <Grid item xs={12} classes={{ root: classes.detailsBlock }}>
                    <Typography classes={{ root: classes.caption }} variant="subtitle2" gutterBottom={true}>
                      {file?.caption ? file.caption : <>&nbsp;</>}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </div>
        </Grid>
      )
    }
  )
)

const SortableGridContainer: React.ComponentType<any> = SortableContainer(
  ({
    files,
    disabled,
    showUploadDropzone,
    onUploadClick,
    uploadButtonPosition,
    inPresentation,
    columns,
    mobileColumns,
    showBorder,
    fullItemHeight,
    showDetails,
    isSettings,
    isEditable,
    handleEditFile,
    mediaOptionsProps,
    presentationLibrary,
    justify,
    onAddFiles,
    onUpload,
    onClick,
    onDelete,
    onEdit,
    onLoadMore,
    selectedIndexes,
    classes,
    filePreviewProps
  }: {
    files: QuoteFile[]
    disabled: boolean
    showUploadDropzone?: boolean
    onUploadClick?: (event: React.MouseEvent) => void
    uploadButtonPosition?: 'start' | 'end'
    columns: number
    mobileColumns: number
    showBorder: boolean
    fullItemHeight: boolean
    showDetails: boolean
    isSettings: boolean
    isEditable: boolean
    inPresentation: boolean
    handleEditFile: (field: string, content: QuoteFile) => void
    mediaOptionsProps?: {
      blockPrefix: string
    }
    justify: 'space-around' | 'space-between' | 'center' | 'flex-start' | 'flex-end'
    classes: any
    filePreviewProps?: Partial<FilePreviewProps>
    presentationLibrary?: {
      addFilesToLibrary: (args: { files: QuoteFile[] }) => void
      library: PresentationLibrary
      saveLibrary: (args: { library: PresentationLibrary }) => void
    }

    onAddFiles?: (files: QuoteFile[]) => void
    onUpload?: (files: File[]) => any
    onClick?: (index: number) => any
    onDelete?: (index: number) => any
    onEdit?: (index: number) => void
    onLoadMore?: () => any
    selectedIndexes?: number[]
  }) => {
    const { online } = useConnection()
    const { quote } = useQuote()
    const { isTrial } = useUser()
    const [loadingMore, setLoadingMore] = React.useState(false)

    // calculate initial number of quote files so we know how many files are left without needing
    // to update the quote first
    const numQuoteFiles = useMemo(() => getFiles({ quote, attached: true }).length - files.length, [quote])

    const isMobile = useMediaQuery('sm')
    const responsiveSpacing = isMobile ? 3 : 6
    // the presence of mediaOptionsProps means this component is in a presentation
    const spacing = mediaOptionsProps ? responsiveSpacing : 3

    const { columnWidth } = getColumnWidth(columns, classes)
    const { columnWidth: mobileColumnWidth } = getColumnWidth(mobileColumns, classes)

    const canAddFiles = !isTrial || numQuoteFiles + files.length < TRIAL_QUOTE_FILES_LIMIT

    const uploadButton = onUploadClick ? (
      <Grid item key={'upload'} xs={mobileColumns ? mobileColumnWidth : 12} sm={columnWidth} md={columnWidth}>
        <Button
          variant="dialog"
          startIcon={<PublishIcon />}
          className={classes.addButton}
          fullWidth
          onClick={onUploadClick}
        >
          Add Media
        </Button>
      </Grid>
    ) : null

    return (
      <Grid container spacing={spacing} alignItems={'stretch'} justifyContent={justify} className={classes.root}>
        {uploadButtonPosition === 'start' && uploadButton}
        {files.map((file, index) => (
          <SortableFilePreview
            key={getFileKey(file, index)}
            isSettings={isSettings}
            isEditable={isEditable}
            handleEditFile={handleEditFile}
            mediaOptionsProps={mediaOptionsProps}
            index={index}
            fileIndex={index}
            file={file}
            showBorder={showBorder}
            inPresentation={inPresentation}
            fullItemHeight={fullItemHeight}
            presentationLibrary={presentationLibrary}
            showDetails={showDetails}
            columns={columns}
            mobileColumns={mobileColumns}
            disabled={disabled}
            onAddFiles={onAddFiles}
            onDelete={onDelete}
            onEdit={onEdit}
            onClick={onClick ? () => onClick(index) : undefined}
            selected={selectedIndexes?.includes(index)}
            filePreviewProps={filePreviewProps}
          />
        ))}
        {uploadButtonPosition !== 'start' && uploadButton}
        {onLoadMore && (
          <Grid item key={'loadMore'} xs={12} sm={12} md={12} className={classes.grid6}>
            <Button
              // Same escape hatch FilesGridNext's Load More uses - without it, a click on a
              // non-editable quote is captured by EditableQuote's onClickCapture and opens the
              // "enter edit mode" prompt instead of reaching this button (#14984).
              id="allow-propagation"
              onClick={async () => {
                setLoadingMore(true)
                await onLoadMore()
                setLoadingMore(false)
              }}
              variant="outlined"
              fullWidth
              loading={loadingMore}
              disabled={!online}
            >
              Load More
            </Button>
          </Grid>
        )}
        {showUploadDropzone && (
          <Grid item key={'uploadDropzone'} xs={12} sm={12} md={12} className={classes.grid6}>
            <Tooltip title={!canAddFiles ? 'You must upgrade your plan to add more media' : ''}>
              <div>
                <UploadDropzone onUpload={onUpload} disabled={!canAddFiles || !online} />
              </div>
            </Tooltip>
          </Grid>
        )}
      </Grid>
    )
  }
)

function UploadDropzone(props: { onUpload?: (files: File[]) => any } & Partial<DropzoneProps>) {
  return (
    <Dropzone
      accept="image/*,video/*"
      text="Add Media"
      onDropAccepted={props.onUpload}
      style={{ height: 'initial', marginTop: 20 }}
      {...props}
    />
  )
}

function PresentationFilesGrid(props: PresentationFilesGridPropsWithInjections) {
  const {
    files,
    isSettings,
    isEditable,
    handleEditFile,
    mediaOptionsProps,
    editable,
    selectedIndexes,
    onAddFiles,
    showUploadDropzone,
    onUploadClick,
    uploadButtonPosition = 'end',
    onUpload,
    columns = 4,
    mobileColumns,
    showBorder = true,
    showDetails = true,
    justify = 'flex-start',
    classes,
    fullItemHeight = false,
    onReorder,
    onFileClick,
    inPresentation,
    onDelete,
    onEdit,
    onLoadMore,
    filePreviewProps,
    presentationLibrary
  } = props

  function handleSortEnd({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) {
    const reorderedFiles = arrayMove(files, oldIndex, newIndex)

    if (onReorder) {
      onReorder(reorderedFiles)
    }
  }

  return (
    <SortableGridContainer
      axis="xy"
      classes={classes}
      columns={columns}
      disabled={!editable}
      filePreviewProps={filePreviewProps}
      files={files}
      fullItemHeight={fullItemHeight}
      handleEditFile={handleEditFile}
      helperClass={classes.dragging}
      inPresentation={inPresentation}
      isEditable={isEditable}
      isSettings={isSettings}
      justify={justify}
      mediaOptionsProps={mediaOptionsProps}
      mobileColumns={mobileColumns}
      onAddFiles={onAddFiles}
      onClick={onFileClick}
      onDelete={onDelete}
      onEdit={onEdit}
      onLoadMore={onLoadMore}
      onSortEnd={handleSortEnd}
      onUpload={onUpload}
      onUploadClick={onUploadClick}
      uploadButtonPosition={uploadButtonPosition}
      presentationLibrary={presentationLibrary}
      pressDelay={300}
      selectedIndexes={selectedIndexes}
      showBorder={showBorder}
      showDetails={showDetails}
      showUploadDropzone={showUploadDropzone}
    />
  )
}

export default withStyles(styles)(PresentationFilesGrid)
